import * as React from 'react';
import SvgEditService from '@/services/svg-edit-service';
import { extractColourFrom } from '@/common/element-colours';
import { SvgAnnotations } from '../svg-annotations';
import { isPDComponentElement } from '@/common/pdUtils';
import { ColourType } from '@/common/colours';

type StrokeLineJoin = 'miter' | 'round' | 'bevel';
type StrokeLineCap = 'butt' | 'round' | 'square';

interface State {
    elementID: string;

    isColourableElement: boolean;

    fillColour: IColourData;
    fillOverprint: boolean;
    fillEvenOddWinding: boolean;

    strokeColour: IColourData;
    strokeOverprint: boolean;

    strokeWidth: number;
    strokeStyle: string;
    strokeLineJoin: StrokeLineJoin;
    strokeLineCap: StrokeLineCap;
    strokeMiterLimit: number;

    strokeSplitH: number;
    strokeSplitV: number;

    marginLeft: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
}

const GetDefaultState = (): State => {
    const s: State = {
        elementID: "",

        isColourableElement: false,

        fillColour: { type: ColourType.Registration, reg: 100, alpha: 1.0 },
        fillOverprint: false,
        fillEvenOddWinding: false,

        strokeColour: { type: ColourType.Registration, reg: 100, alpha: 1.0 },
        strokeOverprint: false,
        strokeWidth: 1.0,
        strokeStyle: "none",
        strokeLineJoin: "miter",
        strokeLineCap: "butt",
        strokeMiterLimit: 4,

        strokeSplitH: 0,
        strokeSplitV: 0,

        marginLeft: 0,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
    };

    return s;
}

const GetStateForSelectedElement = (el: SVGElement) => {
    const newState = GetDefaultState();

    let elID = el.getAttribute("id");
    if (!elID || elID.length == 0) {
        elID = SvgEditService.GetNextElementID();
        el.setAttribute("id", elID);
    }
    newState.elementID = elID;
    const elType = el.tagName.toLowerCase();

    if (elType === "line") {
        newState.fillColour = { type: ColourType.None };
    } else {
        const fillCol = extractColourFrom(el, "fill");
        newState.fillColour = fillCol.colour ?? newState.fillColour;
        newState.fillOverprint = fillCol.overprint;
        newState.fillEvenOddWinding = (el.getAttribute("fill-rule") ?? "").toLowerCase() === "evenodd";
    }

    const strokeCol = extractColourFrom(el, "stroke");
    newState.strokeColour = strokeCol.colour ?? newState.strokeColour;
    newState.strokeOverprint = strokeCol.overprint;
    newState.strokeWidth = Number(el.getAttribute("stroke-width") ?? newState.strokeWidth);
    newState.strokeStyle = el.getAttribute("stroke-dasharray") ?? newState.strokeStyle;
    newState.strokeLineJoin = (el.getAttribute("stroke-linejoin") ?? newState.strokeLineJoin) as StrokeLineJoin;
    newState.strokeLineCap = (el.getAttribute("stroke-linecap") ?? newState.strokeLineCap) as StrokeLineCap;
    newState.strokeMiterLimit = Number(el.getAttribute("stroke-miterlimit") ?? newState.strokeMiterLimit);
    newState.strokeSplitH = Number.parseInt(el.getAttribute("pe-split-horizontal") ?? newState.strokeSplitH.toString());
    newState.strokeSplitV = Number.parseInt(el.getAttribute("pe-split-vertical") ?? newState.strokeSplitV.toString());

    newState.marginLeft = Number.parseFloat(el.getAttribute("pe-margin-left") ?? newState.marginLeft.toString());
    newState.marginTop = Number.parseFloat(el.getAttribute("pe-margin-top") ?? newState.marginTop.toString());
    newState.marginRight = Number.parseFloat(el.getAttribute("pe-margin-right") ?? newState.marginRight.toString());
    newState.marginBottom = Number.parseFloat(el.getAttribute("pe-margin-bottom") ?? newState.marginBottom.toString());

    return newState;
}

SvgAnnotations.RegisterAnnotationCallback("COLORPICKER", ({ bbox, element, svgAnnotations, textAnnotations, colourService }) => {
    const elState = GetStateForSelectedElement(element);

    const addColourString = (prefix: "Stroke" | "Fill") => {
        const colour = prefix === "Stroke" ? elState.strokeColour : elState.fillColour;

        switch (colour.type) {
            case ColourType.Registration:
                textAnnotations.splice(0, 0, `${prefix} Colour: Registration at ${colour.reg}%`);
                break;

            case ColourType.RegisteredSpot:
                if (colour.spotID.trim().length > 0) {
                    const spot = colourService.GetSpotColourWithIdOrName(colour.spotID);
                    const spotName = spot?.name ?? `UNKNOWN - ID: ${colour.spotID}`;
                    textAnnotations.splice(0, 0, `${prefix} Colour: Spot Colour '${spotName}'`);
                }
                break;

            case ColourType.ProcessColour:
                textAnnotations.splice(0, 0, `${prefix} Colour: Process Colour C: ${colour.c}, M: ${colour.m}, Y: ${colour.y}, K: ${colour.k}`);
                break;

            case ColourType.CustomSpot:
                textAnnotations.splice(0, 0, `${prefix} Colour: Custom Spot Colour '${colour.name}'`);
                break;
        }
    }

    if (!isPDComponentElement(element)) {
        // Due to splice, these will display in the reverse order, fill then stroke. This matches the rest of the UI.
        addColourString("Stroke");
        addColourString("Fill");
    }

    if (elState.strokeSplitH > 0) {
        const mul = 1 / (elState.strokeSplitH + 1) * bbox.height;

        for (let i = 1; i <= elState.strokeSplitH; ++i) {
            const top = bbox.top + i * mul;
            svgAnnotations.push((
                <line key={`splitH_${i}`} x1={bbox.left} y1={top} x2={bbox.right} y2={top} stroke="rgb(34, 34, 204, 0.7)" strokeWidth={1} pointerEvents="none" />
            ));
        }
    }

    if (elState.strokeSplitV > 0) {
        const mul = 1 / (elState.strokeSplitV + 1) * bbox.width;

        for (let i = 1; i <= elState.strokeSplitV; ++i) {
            const left = bbox.left + i * mul;
            svgAnnotations.push((
                <line key={`splitV_${i}`} x1={left} y1={bbox.top} x2={left} y2={bbox.bottom} stroke="rgb(34, 34, 204, 0.7)" strokeWidth={1} pointerEvents="none" />
            ));
        }
    }
});

SvgAnnotations.RegisterAnnotationCallback("ELEMENTDETAILS", ({ svgEditService, element, svgAnnotations, defs }) => {
    if (!element) {
        return;
    }

    const zoomLevel = svgEditService.canvas.getCurrentZoom();

    const marginLeft = parseFloat(element.getAttribute("pe-margin-left") ?? "0");
    const marginTop = parseFloat(element.getAttribute("pe-margin-top") ?? "0");
    const marginRight = parseFloat(element.getAttribute("pe-margin-right") ?? "0");
    const marginBottom = parseFloat(element.getAttribute("pe-margin-bottom") ?? "0");

    if (marginLeft > 0 || marginTop > 0 || marginRight > 0 || marginBottom > 0) {
        defs.push((
            <pattern key="elMargin_hatchPatternDef" id="element-margin-hatch" width="10" height="10" patternUnits="userSpaceOnUse">
                <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 10,10" />
                <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 0,10" />
                <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 10,0" />
                <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 0,0" />
            </pattern>
        ));

        const svgcontent = document.getElementById("svgcontent") as unknown as SVGSVGElement;
        const docX = svgcontent.x.baseVal.value - SvgAnnotations.OffsetX;
        const docY = svgcontent.y.baseVal.value - SvgAnnotations.OffsetY;

        const leftEdge = (svgEditService.canvas.contentW - marginRight) * zoomLevel;
        const topEdge = (svgEditService.canvas.contentH - marginBottom) * zoomLevel;
        const rightEdge = svgEditService.canvas.contentW * zoomLevel;
        const bottomEdge = svgEditService.canvas.contentH * zoomLevel;

        if(marginBottom > 0) {
            svgAnnotations.push((
                <rect key="elMargin_bottomDangerFill" x={docX} y={docY + topEdge} width={rightEdge} height={marginBottom * zoomLevel} fill="url(#element-margin-hatch)" pointerEvents="none" />
            ));
            svgAnnotations.push((
                <line key="elMargin_bottomDangerStroke" x1={docX} y1={docY + topEdge} x2={docX + rightEdge} y2={docY + topEdge} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
            ));
        }

        if(marginRight > 0) {
            svgAnnotations.push((
                <rect key="elMargin_rightDangerFill" x={docX + leftEdge} y={docY} width={marginRight * zoomLevel} height={bottomEdge} fill="url(#element-margin-hatch)" pointerEvents="none" />
            ));
            svgAnnotations.push((
                <line key="elMargin_rightDangerStroke" x1={docX + leftEdge} y1={docY} x2={docX + leftEdge} y2={docY + bottomEdge} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
            ));
        }

        if(marginTop > 0) {
            svgAnnotations.push((
                <rect key="elMargin_topDangerFill" x={docX} y={docY} width={rightEdge} height={marginTop * zoomLevel} fill="url(#element-margin-hatch)" pointerEvents="none" />
            ));
            svgAnnotations.push((
                <line key="elMargin_topDangerStroke" x1={docX} y1={docY + marginTop * zoomLevel} x2={docX + rightEdge} y2={docY + marginTop * zoomLevel} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
            ));
        }

        if(marginLeft > 0) {
            svgAnnotations.push((
                <rect key="elMargin_leftDangerFill" x={docX} y={docY} width={marginLeft * zoomLevel} height={bottomEdge} fill="url(#element-margin-hatch)" pointerEvents="none" />
            ));
            svgAnnotations.push((
                <line key="elMargin_leftDangerStroke" x1={docX + marginLeft * zoomLevel} y1={docY} x2={docX + marginLeft * zoomLevel} y2={docY + bottomEdge} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
            ));
        }
    }
});
